import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import {
  BILLING_PAYMENT_PENDING_KEY,
  billingStorageKey,
  removeStoredBillingValue,
  writeStoredBillingValue,
} from '../utils/billingCheckout';

const stripePromises = new Map();

const stripePromiseForKey = (publishableKey) => {
  if (!stripePromises.has(publishableKey)) {
    stripePromises.set(publishableKey, loadStripe(publishableKey));
  }
  return stripePromises.get(publishableKey);
};

const safeStripeError = (error) => {
  if (
    error
    && ['card_error', 'validation_error'].includes(error.type)
    && typeof error.message === 'string'
    && error.message.length <= 300
  ) {
    return error.message;
  }
  return 'Payment could not be confirmed. Please try again.';
};

const appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#00E5FF',
    colorBackground: '#121816',
    colorText: '#FFFFFF',
    colorDanger: '#FF2D95',
    borderRadius: '16px',
    fontFamily: '"Press Start 2P", monospace',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      border: '1px solid rgba(0, 229, 255, 0.65)',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #39FF14',
      boxShadow: '0 0 10px rgba(57, 255, 20, 0.22)',
    },
    '.Label': {
      color: '#00E5FF',
    },
  },
};

const validCreationResponse = (response) => (
  response?.status === 'incomplete'
  && response?.requires_confirmation === true
  && typeof response?.client_secret === 'string'
  && Boolean(response.client_secret)
);

const PaymentForm = ({
  amountLabel,
  clientSecret,
  createSubscription,
  username,
  onProcessing,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const submittingRef = useRef(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);
    setErrorMessage('');
    const pendingStorageKey = billingStorageKey(
      BILLING_PAYMENT_PENDING_KEY,
      username,
    );

    const submitted = await elements.submit();
    if (submitted?.error) {
      setErrorMessage(safeStripeError(submitted.error));
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    let confirmationClientSecret = clientSecret;
    if (!confirmationClientSecret) {
      try {
        const response = await createSubscription();
        if (!validCreationResponse(response)) {
          throw new Error('invalid_subscription_response');
        }
        confirmationClientSecret = response.client_secret;
      } catch (error) {
        setErrorMessage(
          error?.data?.message
          || 'Unable to start secure payment. Please try again.',
        );
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }
    }

    writeStoredBillingValue(pendingStorageKey, {
      username,
      startedAt: Date.now(),
    });

    let result;
    try {
      result = await stripe.confirmPayment({
        elements,
        clientSecret: confirmationClientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/profile`,
        },
        redirect: 'if_required',
      });
    } catch (_error) {
      removeStoredBillingValue(pendingStorageKey);
      setErrorMessage('Payment could not be confirmed. Please try again.');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (result?.error) {
      removeStoredBillingValue(pendingStorageKey);
      setErrorMessage(safeStripeError(result.error));
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    onProcessing();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'grid', gap: 2 }}>
      <PaymentElement options={{ layout: 'tabs' }} />
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Button
        type="submit"
        variant="contained"
        disabled={!stripe || !elements || submitting}
      >
        {submitting ? 'Confirming...' : `Pay ${amountLabel} and Subscribe`}
      </Button>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
        Leaving or refreshing before then saves nothing. Your subscription starts only after you select Pay.
      </Typography>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
        Dopatize does not store your payment information. All payments are securely processed by Stripe.
      </Typography>
    </Box>
  );
};

const SubscriptionCheckout = ({
  amount,
  amountLabel,
  clientSecret = null,
  createSubscription,
  currency,
  planKey,
  publishableKey,
  username,
  onProcessing,
}) => {
  const stripePromise = useMemo(
    () => stripePromiseForKey(publishableKey),
    [publishableKey],
  );
  const options = useMemo(() => (
    clientSecret
      ? {
        clientSecret,
        appearance,
        loader: 'auto',
      }
      : {
        mode: 'subscription',
        amount,
        currency: String(currency || 'usd').toLowerCase(),
        paymentMethodTypes: ['card'],
        appearance,
        loader: 'auto',
      }
  ), [amount, clientSecret, currency]);
  const elementsKey = clientSecret || `${planKey}:${amount}:${currency}`;

  return (
    <Elements stripe={stripePromise} options={options} key={elementsKey}>
      <PaymentForm
        amountLabel={amountLabel}
        clientSecret={clientSecret}
        createSubscription={createSubscription}
        username={username}
        onProcessing={onProcessing}
      />
    </Elements>
  );
};

export default SubscriptionCheckout;
